import clsx from "clsx";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export type SegmentedTabItem<T extends string = string> = {
  readonly value: T;
  readonly label: string;
};

const shellClassName = "flex rounded-lg border border-gray-200 bg-white p-0.5";
const tabButtonBaseClass = "border-none shadow-none";
const tabButtonActiveClass = "bg-brand700 hover:bg-brand700/90 text-white";

export type FilterSegmentedTabsProps<T extends string> = {
  readonly tabs: readonly SegmentedTabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Badge counts per tab value; missing keys show 0. */
  counts?: Partial<Record<T, number>>;
  /** Merged after shell + layout (e.g. width / flex direction). */
  className?: string;
  /** Accessible name for the tablist. */
  ariaLabel: string;
};

/**
 * Generic BO segmented control: equal-styled tabs with optional count badges.
 */
export function FilterSegmentedTabs<T extends string>({
  tabs,
  value,
  onChange,
  counts,
  className,
  ariaLabel,
}: FilterSegmentedTabsProps<T>) {
  return (
    <div className={clsx(shellClassName, className)} role="tablist" aria-label={ariaLabel}>
      {tabs.map(({ value: tabValue, label }) => (
        <Button
          key={tabValue}
          type="button"
          variant="outline"
          role="tab"
          aria-selected={value === tabValue}
          onClick={() => onChange(tabValue)}
          className={clsx(tabButtonBaseClass, value === tabValue && tabButtonActiveClass)}
        >
          {label}
          <Badge variant="destructive">{counts?.[tabValue] ?? 0}</Badge>
        </Button>
      ))}
    </div>
  );
}
